/**
 * Browser replacement for expo-location.
 * Positions come from the Geolocation API; place names from OpenStreetMap's
 * Nominatim reverse-geocoder (same tile provider the map picker uses).
 */

export type Coords = { latitude: number; longitude: number };

export const DEFAULT_COORDS: Coords = { latitude: 33.5731, longitude: -7.5898 }; // Casablanca

export function getCurrentPosition(): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("La géolocalisation n'est pas disponible sur ce navigateur."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      (error) => {
        const message =
          error.code === error.PERMISSION_DENIED
            ? 'Autorisez la localisation pour utiliser votre position actuelle.'
            : 'Impossible de récupérer votre position pour le moment.';
        reject(new Error(message));
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  });
}

type NominatimAddress = {
  road?: string;
  neighbourhood?: string;
  suburb?: string;
  city?: string;
  town?: string;
  village?: string;
  county?: string;
  state?: string;
};

type NominatimResponse = {
  display_name?: string;
  address?: NominatimAddress;
};

async function reverseGeocode(coords: Coords): Promise<NominatimResponse | null> {
  try {
    const url =
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
      `&lat=${coords.latitude}&lon=${coords.longitude}&zoom=18&accept-language=fr`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return (await response.json()) as NominatimResponse;
  } catch {
    return null;
  }
}

const coordsLabel = (coords: Coords) =>
  `Point choisi: ${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`;

/** Human-readable street address for a point, falling back to raw coordinates. */
export async function describeCoords(coords: Coords): Promise<string> {
  const result = await reverseGeocode(coords);
  const address = result?.address;
  if (!address) return result?.display_name || coordsLabel(coords);

  const parts = [
    address.road,
    address.neighbourhood ?? address.suburb,
    address.city ?? address.town ?? address.village,
    address.state,
  ].filter(Boolean) as string[];

  if (parts.length === 0) return result?.display_name || coordsLabel(coords);
  return Array.from(new Set(parts)).join(', ');
}

/** City name for a point — used to prefill provider profiles. */
export async function detectCity(coords: Coords): Promise<string> {
  const result = await reverseGeocode(coords);
  const address = result?.address;
  return (
    address?.city ??
    address?.town ??
    address?.village ??
    address?.county ??
    address?.state ??
    ''
  );
}
